import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { takeReturnTo } from '@/lib/oauth';
import { onIdentityChanged } from '@/lib/session-change';

/** One value off the current query string, or undefined. It is a server's string, not a route's. */
function searchParam(search: unknown, key: string): string | undefined {
  const value = (search as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : undefined;
}

/**
 * The far end of the Google redirect dance.
 *
 * `GET /auth/google/callback` finishes by sending the browser back to `WEB_APP_URL` with the
 * session cookie already set and `?auth=google` — or `?auth=google_failed&reason=…` — on the
 * URL. Nothing here would notice on its own: the cookie is httpOnly, so `auth.me` has to be
 * asked again before the player is signed in as far as this client is concerned. Without that
 * the landing is a signed-out page that only a manual reload fixes, which is the bug.
 *
 * It runs in the root layout because the landing URL is whatever route `WEB_APP_URL` names,
 * which is the server's choice and not this client's.
 *
 * It is the whole cache that is reset rather than `auth.me` alone, for `onIdentityChanged`'s
 * reason: the browser that comes back from Google may be a different person to the one that
 * left, and everything cached in between the two is an answer about the one that left.
 */
export function useGoogleAuthLanding(): void {
  const navigate = useNavigate();
  // Not `useSearch`: no route of ours declares these, and the root route validates no search.
  const auth = useRouterState({ select: (s) => searchParam(s.location.search, 'auth') });
  const reason = useRouterState({ select: (s) => searchParam(s.location.search, 'reason') });
  const queryClient = useQueryClient();

  useEffect(() => {
    // `replace` throughout: the outcome is good for one landing, and Back should not replay it.
    if (auth === 'google_failed') {
      navigate({ to: '/login', search: { reason }, replace: true });
      return;
    }
    if (auth !== 'google') return;

    const returnTo = takeReturnTo();
    void (async () => {
      await onIdentityChanged(queryClient);
      navigate({ to: returnTo, replace: true });
    })();
  }, [auth, reason, navigate, queryClient]);
}
